import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import { prepareAuthenticatedHeaders } from './utils/headers'
import type {
  Locations,
  Tags,
  JobTitles,
  Domains,
  Themes,
  Keywords,
  Pagination,
  PageOptions,
  Config,
  Laboraties,
} from '../types'

export const DEFAULT_PAGE_SIZE = 500

export const optionsApi = createApi({
  reducerPath: 'optionsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${import.meta.env.VITE_API_URL}/api/v1/`,
    prepareHeaders: prepareAuthenticatedHeaders,
  }),
  tagTypes: ['Config', 'Options', 'Keywords'],
  endpoints: builder => ({
    config: builder.query<Config, void>({
      query: () => ({
        url: 'config/',
      }),
      providesTags: ['Config'],
    }),
    listLocations: builder.query<Locations[], void>({
      query: () => ({
        url: 'locations/',
      }),
      providesTags: ['Options'],
    }),
    listTags: builder.query<Tags[], void>({
      query: () => ({
        url: 'tags/',
      }),
      providesTags: ['Options'],
    }),
    listJobTitles: builder.query<JobTitles[], void>({
      query: () => ({
        url: 'job-titles/',
      }),
      providesTags: ['Options'],
    }),
    listDomains: builder.query<Domains[], void>({
      query: () => ({
        url: 'domains/',
      }),
      providesTags: ['Options'],
    }),
    listThemes: builder.query<Themes[], void>({
      query: () => ({
        url: 'themes/',
      }),
      providesTags: ['Options'],
    }),
    listLaboratories: builder.query<Laboraties[], void>({
      query: () => ({
        url: 'laboratories/',
      }),
    }),
    listKeywords: builder.query<Pagination<Keywords>, Partial<PageOptions> | void>({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        const { page = 1, pageSize = DEFAULT_PAGE_SIZE } = arg || {}
        const result = await fetchWithBQ(`keywords/?page=${page}&page_size=${pageSize}`)

        if ('data' in result && result.data) {
          const data = result.data as Pagination<Keywords>
          let currentPage = page

          while (!!data.next) {
            currentPage++
            const nextResult = await fetchWithBQ(
              `keywords/?page=${currentPage}&page_size=${pageSize}`
            )

            if ('data' in nextResult && nextResult.data) {
              const nextData = nextResult.data as Pagination<Keywords>
              data.results.push(...nextData.results)
              data.next = nextData.next
            }
            if ('error' in nextResult && nextResult.error) {
              break
            }
          }

          data.next = null
          data.count = data.results.length

          return { data: data }
        }

        return 'error' in result && result.error
          ? {
              error: result.error,
            }
          : {
              data: result.data as Pagination<Keywords>,
            }
      },
      providesTags: ['Keywords'],
    }),
    createKeyword: builder.mutation<Keywords, string>({
      query: name => ({
        url: 'keywords/',
        method: 'POST',
        body: { name },
      }),
      invalidatesTags: ['Keywords'],
    }),
    searchKeyword: builder.query<Keywords[], string>({
      query: query => ({
        url: `search/keywords/`,
        method: 'GET',
        body: { query },
      }),
    }),
  }),
})

export const {
  useListLocationsQuery,
  useListJobTitlesQuery,
  useListTagsQuery,
  useListDomainsQuery,
  useListThemesQuery,
  useListKeywordsQuery,
  useLazyListKeywordsQuery,
  useCreateKeywordMutation,
  useSearchKeywordQuery,
} = optionsApi
